import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Chip,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  InputAdornment,
  Popper,
  ClickAwayListener,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  LocalOffer as TagIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

const TagSelector = ({
  selectedTags = [],
  onTagsChange,
  disabled = false,
  placeholder = "Type to search or create tags...",
  maxTags = 10,
  languageCode = null
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [recentTags, setRecentTags] = useState([]);
  const [allUserTags, setAllUserTags] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const inputRef = useRef(null);
  const anchorEl = useRef(null);

  // Load user's tags on mount
  useEffect(() => {
    loadUserTags();
  }, []);

  const loadUserTags = async () => {
    try {
      setInitialLoad(true);
      const response = await apiClient.tags.getUserTags(languageCode);
      const tags = response.data.tags || [];

      setAllUserTags(tags);

      // Get the 5 most recently used tags (highest usage_count)
      const sortedByUsage = [...tags]
        .filter(tag => !selectedTags.some(st => st.id === tag.id))
        .sort((a, b) => b.usage_count - a.usage_count)
        .slice(0, 5);

      setRecentTags(sortedByUsage);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setInitialLoad(false);
    }
  };

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = allUserTags.filter(tag =>
        tag.tag_name.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedTags.some(st => st.id === tag.id || st.tag_name === tag.tag_name)
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [inputValue, allUserTags, selectedTags]);

  // Update recent tags when selected tags change
  useEffect(() => {
    const filtered = recentTags.filter(tag =>
      !selectedTags.some(st => st.id === tag.id || st.tag_name === tag.tag_name)
    );
    setRecentTags(filtered);
  }, [selectedTags]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleAddTag = async (tag) => {
    if (selectedTags.length >= maxTags) {
      return;
    }

    // Check if tag already exists in selected
    if (selectedTags.some(t => t.tag_name === tag.tag_name)) {
      return;
    }

    const newTags = [...selectedTags, tag];
    onTagsChange(newTags);
    setInputValue('');
    setIsOpen(false);

    // Update recent tags
    setRecentTags(prev => prev.filter(t => t.id !== tag.id));
  };

  const handleCreateTag = async () => {
    const tagName = inputValue.trim().toLowerCase();

    if (!tagName || selectedTags.length >= maxTags) {
      return;
    }

    // Check if tag already exists
    if (selectedTags.some(t => t.tag_name === tagName)) {
      setInputValue('');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.tags.createTag({ tag_name: tagName });
      const newTag = response.data.tag;

      const newTags = [...selectedTags, newTag];
      onTagsChange(newTags);
      setInputValue('');
      setIsOpen(false);

      // Add to allUserTags
      setAllUserTags(prev => [...prev, newTag]);
    } catch (error) {
      console.error('Error creating tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = selectedTags.filter(tag =>
      tag.id !== tagToRemove.id && tag.tag_name !== tagToRemove.tag_name
    );
    onTagsChange(newTags);

    // Add back to recent tags if it was there before
    if (tagToRemove.usage_count > 0) {
      setRecentTags(prev => {
        const updated = [...prev, tagToRemove]
          .sort((a, b) => b.usage_count - a.usage_count)
          .slice(0, 5);
        return updated;
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();

      // Check if there's an exact match in suggestions
      const exactMatch = suggestions.find(s =>
        s.tag_name.toLowerCase() === inputValue.toLowerCase()
      );

      if (exactMatch) {
        handleAddTag(exactMatch);
      } else {
        handleCreateTag();
      }
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      // Remove last tag on backspace if input is empty
      handleRemoveTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const showCreateOption = inputValue.trim() &&
    !suggestions.some(s => s.tag_name.toLowerCase() === inputValue.toLowerCase());

  if (initialLoad) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Recent/Quick Tags */}
      {recentTags.length > 0 && selectedTags.length < maxTags && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Recent tags:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {recentTags.map(tag => (
              <Chip
                key={tag.id}
                label={tag.tag_name}
                size="small"
                icon={<AddIcon fontSize="small" />}
                onClick={() => handleAddTag(tag)}
                disabled={disabled}
                sx={{
                  backgroundColor: tag.color ? `${tag.color}20` : 'action.hover',
                  borderColor: tag.color || 'divider',
                  '&:hover': {
                    backgroundColor: tag.color ? `${tag.color}40` : 'action.selected',
                  }
                }}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Selected Tags Display */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        mb: 1,
        minHeight: selectedTags.length === 0 ? 0 : 32
      }}>
        {selectedTags.map((tag) => (
          <Chip
            key={tag.id || tag.tag_name}
            label={tag.tag_name}
            size="small"
            onDelete={() => handleRemoveTag(tag)}
            disabled={disabled}
            sx={{
              backgroundColor: tag.color ? `${tag.color}20` : undefined,
              borderColor: tag.color || 'divider',
            }}
            deleteIcon={<CloseIcon fontSize="small" />}
          />
        ))}
      </Box>

      {/* Input Field */}
      <Box ref={anchorEl}>
        <TextField
          ref={inputRef}
          fullWidth
          size="small"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled || selectedTags.length >= maxTags}
          placeholder={
            selectedTags.length >= maxTags
              ? `Maximum ${maxTags} tags reached`
              : selectedTags.length > 0
                ? "Add another tag..."
                : placeholder
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TagIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: loading && (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Suggestions Dropdown */}
      <Popper
        open={isOpen && (suggestions.length > 0 || showCreateOption)}
        anchorEl={anchorEl.current}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorEl.current?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={3} sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
            <List dense sx={{ p: 0 }}>
              {suggestions.map(tag => (
                <ListItemButton
                  key={tag.id}
                  onClick={() => handleAddTag(tag)}
                  disabled={disabled}
                >
                  <ListItemText
                    primary={tag.tag_name}
                    secondary={tag.usage_count > 0 ? `Used ${tag.usage_count} times` : 'Not used yet'}
                  />
                  {tag.color && (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: tag.color,
                        ml: 1
                      }}
                    />
                  )}
                </ListItemButton>
              ))}

              {showCreateOption && (
                <>
                  {suggestions.length > 0 && <Divider />}
                  <ListItemButton
                    onClick={handleCreateTag}
                    disabled={disabled || loading}
                    sx={{
                      backgroundColor: 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AddIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            Create tag "<strong>{inputValue}</strong>"
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </>
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default TagSelector;